/**
 * Money helpers — Feb 2026 iter-201.
 *
 * User policy: money on this platform (AED) is always TRUNCATED to fils —
 * never rounded up. So 1153.4964 → 1153.49 (not 1153.50). The user is fine
 * with taking one fewer fils per transaction in exchange for guaranteed
 * accuracy against the exact input.
 *
 * All AED calculations and displays must go through `floorAED` (math) and
 * `fmtAED` (render). No `.toFixed(2)`, no `Math.round(x*100)/100` on money.
 *
 * NOTE: intentionally NOT applied to trading (broker returns pre-computed
 * USD PnL) or to non-currency numbers (lot volumes, SL/TP price distances).
 */

/**
 * Truncate a number to 2 decimal places (drop any fils fraction).
 * Guarantees: floorAED(1153.4964) === 1153.49
 *             floorAED(1000)      === 1000
 *             floorAED(null)      === 0
 * Negative amounts are floored **toward zero** (truncation), not down.
 */
export const floorAED = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  const sign = v < 0 ? -1 : 1;
  // Add a tiny epsilon (1e-6) so numbers like 1234567.89 — stored as
  // 1234567.889999… in binary — aren't incorrectly truncated to .88.
  // The epsilon is 10 000× smaller than a fils, so it can NEVER
  // promote a legitimate `.4964` (difference 0.0036) up to `.50`.
  return (sign * Math.floor(Math.abs(v) * 100 + 1e-6)) / 100;
};

/**
 * Format a number as AED with EXACTLY 2 decimals and comma thousands.
 * Always truncates first — the displayed value equals what the backend stored.
 *   fmtAED(1153.4964) → "1,153.49"
 *   fmtAED(1000)      → "1,000.00"
 *   fmtAED(-42.5)     → "-42.50"
 */
export const fmtAED = (n) => {
  const floored = floorAED(n);
  return floored.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/** Format WITH currency suffix (default د.إ). */
export const fmtAEDCurrency = (n, ar = true) =>
  `${fmtAED(n)} ${ar ? 'د.إ' : 'AED'}`;

// iter-217c-v11 (Feb 2026) — User directive: drop the text "AED"/"د.إ"
// EVERYWHERE and use the dirham currency symbol instead, always on the
// left of the number regardless of language. Prefer the `<DirhamPrice>`
// React component from `components/DirhamPrice.jsx` for new code:
//
//   <DirhamPrice value={4200} decimals={2} />              // white icon (dark bg)
//   <DirhamPrice value={4200} theme="light" />             // black icon (light bg)
//
// The helpers above (`fmtAED`, `fmtAEDCurrency`) are kept for string
// contexts (window.confirm, toast text, PDF text, alt attributes …) but
// visual UI should render `<DirhamPrice>` instead.
